import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { shareTicketDocumentAttachments } from '~/api/tickets';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import { useAppSelector } from '~/rootState';
import * as toastTypes from '~/types/toast.types';
import { coerceFileType } from '~/utils/files';

import OutboundEmailModal from '../../Modals/EmailModal/OutboundEmailModal';

interface ShareDocumentButtonProps {
  iconOnly?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  onShare: () => void;
}

function ShareDocumentButton({
  iconOnly = false,
  isDisabled,
  tooltip,
  onShare,
}: ShareDocumentButtonProps) {
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState<unknown>();
  const dispatch = useDispatch();
  const ticket = useAppSelector((state) => state.ticket);

  const defaultTooltip = intl.formatMessage({
    id: 'ShareDocumentButton.defaultTooltip',
    defaultMessage: 'Share current version',
    description: 'Default tooltip text for share button',
  });

  function handleShowModal() {
    setShowModal(true);
  }

  function handleHideModal() {
    setShowModal(false);
  }

  const { name, type } = ticket.document as { name: string; type: string };
  const attachments = type ? [{ name, type: coerceFileType(type || '') }] : [];
  const actionButton = {
    text: intl.formatMessage({
      id: 'ShareDocumentButton.share',
      defaultMessage: 'Share',
      description: 'Share button text',
    }),
    variant: 'primary',
    errorHandler: (e: { response?: { data?: { data?: string } } }) => {
      const defaultErrorMessage = intl.formatMessage({
        id: 'ShareDocumentButton.defaultError',
        defaultMessage: 'Unable to share document, please try again',
        description: 'Default error message when sharing fails',
      });
      const errorMessage = e?.response?.data?.data || defaultErrorMessage;
      showToast(toastTypes.ERROR, errorMessage);
    },
    promise: async function () {
      if (data) {
        await shareTicketDocumentAttachments({ data, ticketId: ticket.id });
        dispatch(api.util.invalidateTags([TAGS.TURN_TRACKING_TAG]));
        onShare();
        showToast(
          toastTypes.SUCCESS,
          intl.formatMessage({
            id: 'ShareDocumentButton.successMessage',
            defaultMessage: 'Document successfully shared.',
            description: 'Success message when document is shared',
          }),
        );
      }
    },
  };
  return (
    <>
      {iconOnly ? (
        <IconButton
          disabled={isDisabled}
          icon="share"
          text={intl.formatMessage({
            id: 'ShareDocumentButton.share',
            defaultMessage: 'Share',
            description: 'Share button text',
          })}
          tooltip={tooltip === '' ? undefined : (tooltip ?? defaultTooltip)}
          onClick={handleShowModal}
        />
      ) : (
        <Button
          disabled={isDisabled}
          icon="share"
          text={intl.formatMessage({
            id: 'ShareDocumentButton.share',
            defaultMessage: 'Share',
            description: 'Share button text',
          })}
          tooltip={tooltip === '' ? undefined : (tooltip ?? defaultTooltip)}
          onClick={handleShowModal}
        />
      )}
      <OutboundEmailModal
        actionButton={actionButton}
        attachments={attachments}
        title={intl.formatMessage({
          id: 'ShareDocumentButton.modalTitle',
          defaultMessage: 'Share Document',
          description: 'Title for share document modal',
        })}
        visible={showModal}
        onHide={handleHideModal}
        onUpdate={setData}
      />
    </>
  );
}

export default ShareDocumentButton;
